import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users } from 'lucide-react';
import { IITH, SERIF } from '@/pages/landing/iithBrand';

function initial(name) {
  return ((name || '?').trim()[0] || '?').toUpperCase();
}

/** Compact you → dependents preview on patient home (no hearts). */
export default function PatientFamilyPreview({ members = [], selfId, primary = IITH.saffron }) {
  const self = members.find((m) => m.id === selfId) || members[0];
  const dependents = useMemo(
    () => members.filter((m) => m.id !== self?.id),
    [members, self],
  );

  return (
    <section
      className="mb-10 rounded-3xl border border-[#D5DEE8] bg-white p-5 sm:p-6 shadow-sm"
      data-testid="patient-family-preview"
    >
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-bold text-[#071B34]" style={{ fontFamily: SERIF }}>Your family</h2>
          <p className="text-xs text-[#7A8B9C]">You at the top — dependents below</p>
        </div>
        <Link to="/patient/family" className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: primary }}>
          Open full tree <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      {!self ? (
        <p className="flex items-center gap-2 text-sm text-[#7A8B9C]">
          <Users className="h-4 w-4" /> No patient profile linked yet.
        </p>
      ) : (
        <div className="flex flex-col items-center py-2">
          <AvatarBubble name={self.name} accent={primary} label="You" />
          {dependents.length > 0 ? (
            <>
              <div className="my-1 h-5 w-0.5" style={{ background: IITH.gold }} aria-hidden />
              {dependents.length > 1 ? (
                <div className="mb-1 h-0.5 w-24" style={{ background: IITH.gold }} aria-hidden />
              ) : null}
              <div className="flex flex-wrap justify-center gap-3">
                {dependents.map((m) => (
                  <Link
                    key={m.id}
                    to={`/patient/care?patient_id=${m.id}`}
                    className="group flex w-[68px] flex-col items-center"
                  >
                    <span
                      className="flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold text-white"
                      style={{ background: IITH.navyMid }}
                    >
                      {initial(m.name)}
                    </span>
                    <span className="mt-1 w-full truncate text-center text-[11px] font-medium text-[#071B34] group-hover:underline">
                      {m.name?.split(' ')[0]}
                    </span>
                    <span className="text-[9px] uppercase text-[#9AADBE]">{m.relationship || 'family'}</span>
                  </Link>
                ))}
              </div>
            </>
          ) : null}
        </div>
      )}
    </section>
  );
}

function AvatarBubble({ name, accent, label }) {
  return (
    <div className="flex flex-col items-center">
      <span
        className="flex h-14 w-14 items-center justify-center rounded-full text-lg font-bold text-white shadow"
        style={{ background: accent }}
      >
        {initial(name)}
      </span>
      <p className="mt-1.5 max-w-[90px] truncate text-center text-xs font-semibold text-[#071B34]">{name}</p>
      <p className="text-[9px] uppercase text-[#9AADBE]">{label}</p>
    </div>
  );
}
